import { ACCOUNT_MODULES, EXPIRED_LINK } from '_constants';
import { getProfilePermissions, getDashboardInfoSections } from 'utils/custom';
import * as TYPES from './Auth.types';

// Account-level modules that describe the ACCOUNT rather than the signed-in user, so they stay
// valid while logged out. Keeping `new_ui_2026_06` across LOG_OUT is what stops the login page
// rendering the old design for the moment before the anonymous modularity re-fetch lands. Only add
// entries here that the API also returns to anonymous callers -- anything user-specific must be
// cleared on logout.
const ACCOUNT_LEVEL_MODULES = [ACCOUNT_MODULES.NEW_UI_2026_06];

const initialState = {
  role: '',
  email: '',
  firstName: '',
  lastName: '',
  isAuthenticated: false,
  isValidLinkMessage: false,
  appCanStart: false,
  isSuperuser: false,
  permissions: [],
  permissionStatus: false,
  agencies: [],
  stateAddress: '',
  subdomain: '',
  dashboardLinks: [],
  dashboardLinksAccount: [],
  timeZone: '',
  accountProduct: '',
  defaultDirection: null,
  detailedPermissions: [],
  modularityPermissions: [],
  accountModules: [],
};

const reducer = (state = initialState, action = {}) => {
  switch (action.type) {
    case TYPES.SET_TOKEN:
      return {
        ...state,
        token: action.payload,
      };

    case TYPES.START_WITHOUT_TOKEN:
      return {
        ...state,
        appCanStart: true,
      };

    case TYPES.LOG_OUT:
      return {
        ...state,
        token: '',
        isAuthenticated: false,
        appCanStart: true,
        isSuperuser: false,
        // `accountProduct` is deliberately NOT cleared: it describes the account (resolved from the
        // subdomain), not the signed-in user, so it stays valid while logged out and keeps the
        // wordmark on the login page. Logout is a client-side history.push, so nothing re-fetches
        // it in time. See ACCOUNT_LEVEL_MODULES above for the same reasoning applied to the flags.
        permissions: [],
        email: '',
        firstName: '',
        lastName: '',
        agencies: [],
        userId: '',
        userGuideLink: '',
        agenciesList: [],
        secGeogList: [],
        role: '',
        campaignIds: [],
        dashboardLinks: [],
        dashboardLinksAccount: [],
        usersImport: {},
        retailersImport: {},
        subdomain: '',
        stateAddress: '',
        timeZone: '',
        detailedPermissions: [],
        // Drop every per-user flag but keep the account-level ones, so the chrome doesn't flip to
        // the old design and back as the re-fetch resolves.
        modularityPermissions: (state.modularityPermissions || []).filter((item) =>
          ACCOUNT_LEVEL_MODULES.includes(item),
        ),
        accountModules: (state.accountModules || []).filter((item) => ACCOUNT_LEVEL_MODULES.includes(item)),
      };

    case TYPES.SIGN_IN_SUCCESS: {
      const {
        email,
        first_name: firstName,
        last_name: lastName,
        permissions: corePermissions,
        modularity_permissions: modularityPermissions,
        state: stateAddress,
        id,
        subdomain,
        dashboard_user_guide_link: userGuideLink,
      } = action.result;
      const permissions = corePermissions.concat(modularityPermissions);
      return {
        ...state,
        permissions,
        // P20M-1309 — keep the modularity list from the very first response:
        // getIsAppDashboardOnlyUser subtracts it from `permissions` to find the
        // role's own grants, so a GovRAMP-only Visitor is redirected on the
        // Dashboard's first render instead of seeing it flash until
        // /api/profile (which also carries detailedPermissions) comes back.
        modularityPermissions,
        firstName,
        lastName,
        email,
        isAuthenticated: true,
        appCanStart: true,
        stateAddress,
        userId: id,
        userGuideLink,
        subdomain,
      };
    }

    case TYPES.GET_PROFILE_DATA_SUCCESS: {
      const {
        email,
        first_name: firstName,
        last_name: lastName,
        state: stateAddress,
        state_name: stateName,
        subdomain,
        is_superuser: isSuperuser = false,
        dashboard_user_guide_link: userGuideLink,
        campaign_ids: campaignIds,
        modularity_permissions: modularityPermissions,
        dashboard_links: dashboardLinks,
        dashboard_links_of_account: dashboardLinksAccount,
        tz: timeZone,
        account_product: accountProduct,
        account_modules: accountModules = [],
        default_direction: defaultDirection,
      } = action?.result?.data[0]?.attributes;
      const userId = action?.result?.data[0]?.id;
      const retailersImport = action?.result?.data[0]?.relationships?.retailers_imports;
      const usersImport = action?.result?.data[0]?.relationships?.users_imports;
      const { permissions, detailedPermissions } = getProfilePermissions({
        data: action.result.data,
        included: action.result.included,
        modularityPermissions,
      });
      // FIXME: refactor: replace this field in project with 'secGeogList'
      const agencies = action?.result?.included.filter((item) => item.type === 'AggregationPolygon');
      const relationAgencies = action?.result?.data[0]?.relationships?.agencies?.data.map((item) => item.id);
      const includedAgency = action?.result?.included.filter((item) => item.type === 'Agency');
      const agenciesList = relationAgencies.map((item) => {
        return includedAgency.find((elem) => elem.id === item);
      });
      const secGeogList = action?.result?.included.filter((item) => item.type === 'AggregationPolygon');
      const role = action?.result?.included.find((item) => item.type === 'Role');
      return {
        ...state,
        permissions,
        email,
        firstName,
        lastName,
        isAuthenticated: true,
        appCanStart: true,
        isSuperuser,
        agencies,
        stateAddress,
        stateName,
        userId,
        userGuideLink,
        agenciesList,
        secGeogList,
        role,
        campaignIds,
        subdomain,
        modularityPermissions,
        dashboardLinks: getDashboardInfoSections(dashboardLinks),
        dashboardLinksAccount,
        timeZone,
        // The unauthenticated modularity_permissions call is the primary source now, so don't let a
        // profile response that omits account_product blank out a value we already have.
        accountProduct: accountProduct || state.accountProduct,
        accountModules,
        retailersImport,
        usersImport,
        defaultDirection,
        detailedPermissions,
      };
    }
    case TYPES.GET_MODULARITY_PERMISSION_SUCCESS: {
      // httpClient's response interceptor resolves to `response.data`, so `action.result` is
      // ALREADY the response body: `{ data: [...permissions], account_product }`. That means
      // `account_product` sits on `action.result` itself, not on `action.result.data` (which is the
      // permission array). An older API returned a bare array as the whole body, still handled here
      // so `modularityPermissions` stays an array either way -- every consumer relies on that
      // (HasAccess spreads it, useNewUi and the nav helpers call .includes on it).
      const body = action.result;
      const isLegacyShape = Array.isArray(body);
      const permissions = isLegacyShape ? body : body?.data || [];
      // `account_product` is account-level -- the API resolves it from the request subdomain, not
      // from the user -- so this unauthenticated call is what makes the POST / POST Survey / POST
      // Collect wordmark available before login. Fall back to whatever is already in state so an
      // API version that omits the field cannot blank out a value the profile already supplied.
      const accountProduct = (isLegacyShape ? null : body?.account_product) || state.accountProduct;
      return {
        ...state,
        permissionStatus: true,
        modularityPermissions: permissions,
        accountProduct,
      };
    }
    case TYPES.GET_MODULARITY_PERMISSION_FAIL: {
      return {
        ...state,
        permissionStatus: true,
      };
    }
    case TYPES.SIGN_IN_FAIL:
      return {
        ...state,
        isAuthenticated: false,
      };

    case TYPES.IS_VALID_LINK_FAIL: {
      return {
        ...state,
        isValidLinkMessage: EXPIRED_LINK === action?.error?.response?.data?.detail,
      };
    }

    case TYPES.SET_NEW_PASSWORD_FAIL: {
      return {
        ...state,
        isAuthenticated: false,
      };
    }

    default:
      return state;
  }
};

export default reducer;
